import { useErrorHandling } from '@rd-web-markets/shared/dist/hooks/useErrorHandling';
import { api } from '@rd-web-markets/shared/dist/services/service';
import { useCallback, useEffect, useState } from 'react';
import claimSubcontractorsService from './claim_subcontractors.service';
import claimExternalWorkersService from './claim_external_workers.service';
import claimConsumablesService from './claim_consumables.service';
import cloudComputingsService from './cloud_computings.service';
import dataLicencesCostsService from './data_licences_costs.service';
import claimSoftwareConsumablesService from '../claim_software_consumables.service';
import claimEmployeesService from './claim_employees.service';
import independentRndContributionsService from './independent_rnd_contributions.service';
import paymentsToClinicalTrialsService from './payments_to_clinical_trials.service';

// only the services that are actually used in the tables
const servicesPerCostFragmentType = {
  'UkSection::ClaimSubcontractor': claimSubcontractorsService,
  'UkSection::ClaimExternalWorker': claimExternalWorkersService,
  'UkSection::ClaimConsumable': claimConsumablesService,
  'UkSection::CloudComputing': cloudComputingsService,
  'UkSection::DataLicencesCost': dataLicencesCostsService,
  'UkSection::IndependentRndContribution': independentRndContributionsService,
  'UkSection::ClaimSoftwareConsumable': claimSoftwareConsumablesService,
  'UkSection::ClaimEmployee': claimEmployeesService,
  'UkSection::PaymentsToClinicalTrial': paymentsToClinicalTrialsService
};
const costFragmentsService = {
  useFetchChildCostFragments: (claimId, costFragmentType) => {
    const fetchAll = useErrorHandling(useCallback(async commercialProjectId => {
      const response = await costFragmentsService.all(claimId, costFragmentType, commercialProjectId);
      return response.cost_fragments;
    }, [claimId, costFragmentType]));
    return fetchAll;
  },
  useGetAll: (claimId, costFragmentType) => {
    const [costFragments, setCostFragments] = useState(null);
    const [totals, setTotals] = useState(null);
    const fetchAll = useErrorHandling(useCallback(async (claimId, costFragmentType) => {
      const response = await costFragmentsService.all(claimId, costFragmentType);
      setCostFragments(response.cost_fragments);
      setTotals(response.totals);
    }, []));
    useEffect(() => {
      if (claimId) {
        fetchAll(claimId, costFragmentType);
      }
    }, [claimId, costFragmentType, fetchAll]);
    return [costFragments, setCostFragments, fetchAll, totals, setTotals];
  },
  useUpdate: (costFragments, setCostFragments, setTotals) => {
    return useErrorHandling(useCallback(async costFragmentId => {
      const index = costFragments.findIndex(cf => cf.id === costFragmentId);
      const result = await costFragmentsService.update(costFragments[index]);
      const updatedCostFragment = result.cost_fragment;
      setTotals(result.totals);
      setCostFragments(costFragments => {
        const index = costFragments.findIndex(cf => cf.id === costFragmentId);
        costFragments[index] = {
          ...costFragments[index],
          ...updatedCostFragment
        };
        return [...costFragments];
      });
      return updatedCostFragment;
    }, [costFragments, setCostFragments, setTotals]));
  },
  useUpdateFragmentable: (costFragments, setCostFragments, setTotals) => {
    return useErrorHandling(useCallback(async costFragmentId => {
      const costFragment = costFragments.find(cf => cf.id === costFragmentId);
      const updateData = {
        id: costFragment.fragmentable_id,
        claim_id: costFragment.claim_id,
        ...costFragment.fragmentable
      };
      await servicesPerCostFragmentType[costFragment.fragmentable_type].update(updateData);
      const updateCostFragmentResult = await costFragmentsService.get(costFragment);
      const updatedCostFragment = updateCostFragmentResult.cost_fragment;
      const totals = updateCostFragmentResult.totals;
      setTotals(totals);
      setCostFragments(costFragments => {
        const index = costFragments.findIndex(cf => cf.id === costFragmentId);

        // We only update the changed fields because rows can carry table-only UI state
        // such as expansion metadata that should survive a backend refresh.
        costFragments[index] = {
          ...costFragments[index],
          ...updatedCostFragment
        };
        return [...costFragments];
      });
    }, [costFragments, setCostFragments, setTotals]));
  },
  useCreate: (costFragments, setCostFragments, setTotals) => {
    return useErrorHandling(useCallback(async costFragmentId => {
      const index = costFragments.findIndex(cf => cf.id === costFragmentId);
      const costFragment = {
        ...costFragments[index]
      };
      const fragmentable = {
        ...costFragment.fragmentable,
        claim_id: costFragment.claim_id
      };
      delete costFragment.fragmentable;
      const result = await costFragmentsService.create({
        claim_id: costFragment.claim_id,
        cost_fragment: costFragment,
        fragmentable: fragmentable
      });
      const createdCostFragment = result.cost_fragment;
      const totals = result.totals;
      costFragments[index] = createdCostFragment;
      setTotals(totals);
      setCostFragments([...costFragments]);
      return createdCostFragment;
    }, [costFragments, setCostFragments, setTotals]));
  },
  useDelete: onDelete => {
    return useErrorHandling(useCallback(async costFragment => {
      const response = await costFragmentsService.delete(costFragment);
      onDelete(costFragment.id, response.totals);
    }, [onDelete]));
  },
  get: cost_fragment => api.get(`/claims/${cost_fragment.claim_id}/cost_fragments/${cost_fragment.id}`),
  update: cost_fragment => api.update(`/claims/${cost_fragment.claim_id}/cost_fragments/${cost_fragment.id}`, {
    cost_fragment
  }),
  all: function (claimId, costFragmentType) {
    let commercialProjectId = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    return api.get(`/claims/${claimId}/cost_fragments?fragmentable_type=${costFragmentType}${commercialProjectId ? `&commercial_project_id=${commercialProjectId}` : ''}`);
  },
  delete: cost_fragment => api.delete(`/claims/${cost_fragment.claim_id}/cost_fragments/${cost_fragment.id}`),
  create: cost_fragment => api.create(`/claims/${cost_fragment.claim_id}/cost_fragments`, cost_fragment)
};
export default costFragmentsService;